import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';

import { APP_BASE_URL_SOCKET } from '../../../Common/EndPoints';
import { userIdSelector } from '../../../features/authentication/auth-slice';
import { getLocalStorage } from '../../../Utils/storage-utils';
import { CCTVCamera } from './cctvTypes';

interface UseCCTVWebSocketParams {
  cameras: CCTVCamera[];
  enabled?: boolean;
  onUpdate?: (updatedCameras: CCTVCamera[]) => void;
}

/**
 * Custom hook to handle WebSocket connection for live CCTV camera status updates
 */
export function useCCTVWebSocket({ cameras, enabled = true, onUpdate }: UseCCTVWebSocketParams) {
  const userId = useSelector(userIdSelector);
  const websocketRef = useRef<WebSocket | null>(null);
  const camerasRef = useRef<Map<string, CCTVCamera>>(new Map());
  const [isConnected, setIsConnected] = useState(false);

  // Update cameras ref when cameras prop changes
  useEffect(() => {
    const cameraMap = new Map<string, CCTVCamera>();
    cameras.forEach((cam) => {
      cameraMap.set(cam.deviceId, cam);
    });
    camerasRef.current = cameraMap;
  }, [cameras]);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (!enabled || !userId) return;

    // Close existing connection
    if (websocketRef.current) {
      websocketRef.current.close();
    }

    // Build WebSocket URL
    let url = `${APP_BASE_URL_SOCKET}/${userId}`;
    const secretKey = getLocalStorage('id_key') ?? '';
    if (secretKey) {
      url = `${url}/key=${secretKey}`;
    }

    // Create new WebSocket connection
    websocketRef.current = new WebSocket(url);

    websocketRef.current.onopen = () => {
      setIsConnected(true);
      console.log('CCTV WebSocket connected');
    };

    websocketRef.current.onmessage = (event) => {
      if (!event?.data) return;

      try {
        const data = JSON.parse(event.data);
        const deviceId = data.deviceId;
        const packetData = data?.dataLists?.[0]?.data;

        if (!packetData) return;

        // Check if this device is one of our cameras
        const existingCamera = camerasRef.current.get(deviceId);
        if (!existingCamera) return;

        // Determine new status from packet data
        // Based on PFC_In, dataList status, or asset.health
        const pfcIn = (packetData['PFC_In'] || '').toString().toUpperCase();
        const dataListStatus = (data?.dataLists?.[0]?.status || '').toString().toUpperCase();
        const assetHealth = (packetData['asset.health'] || '').toString().toUpperCase();
        const hasRecentData = true; // We just received this data

        const isActive = pfcIn === 'ACTIVE' ||
                         dataListStatus === 'NORMAL' ||
                         assetHealth === 'HEALTHY' ||
                         hasRecentData;

        // Update camera data (simplified - no latency, ipAddress, rawData)
        const updatedCamera: CCTVCamera = {
          ...existingCamera,
          status: isActive ? 'active' : 'inactive',
          lastActivity: new Date().toISOString(),
        };

        // Update local ref
        camerasRef.current.set(deviceId, updatedCamera);

        // Notify parent component
        if (onUpdate) {
          const updatedCameras = Array.from(camerasRef.current.values());
          onUpdate(updatedCameras);
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    websocketRef.current.onclose = (event) => {
      setIsConnected(false);
      if (event?.code === 1008) {
        toast.error(event?.reason || 'WebSocket Unauthorized');
      }
      console.log('CCTV WebSocket closed');
    };

    websocketRef.current.onerror = (error) => {
      console.error('CCTV WebSocket error:', error);
      setIsConnected(false);
    };
  }, [enabled, userId, onUpdate]);

  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    if (websocketRef.current) {
      websocketRef.current.close();
      websocketRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    if (enabled && cameras.length > 0) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [enabled, cameras.length, connect, disconnect]);

  return {
    isConnected,
    connect,
    disconnect,
  };
}

export default useCCTVWebSocket;
